import _memoize from 'lodash/memoize';
import { PRIMARY, makeThemeColor } from '@tornado-rdk/theme';
import { HD, FULL_HD } from '@bedrock-rdk/constants';
import { getFont, FONT } from '@tornado-rdk/typography';
import { middle } from '../../../tornado/utils/sizes';
import { makeDefaultStylingContext } from '../../../tornado/utils/style';
import { BORDER_WIDTH, SCALE_FOCUSED, BORDER_COLOR, INNER_BORDER_COLOR } from '../../../tornado/utils/focus';
import { TRANSPARENT } from '../../../tornado/utils/colors';

export const defaultContext = makeDefaultStylingContext();

export const MOSAIC_CARD_SIZE = {
  [HD]: {
    w: 279,
    h: 135,
  },
  [FULL_HD]: {
    w: 336,
    h: 189,
  },
};

export const PADDING = 8;

export const colors = {
  background: makeThemeColor(PRIMARY, 0.1),
  backgroundFocused: makeThemeColor(PRIMARY, 0.3),
  icon: makeThemeColor(PRIMARY),
  label: makeThemeColor(PRIMARY),
};

export const ICON_SIZES = {
  [HD]: 48,
  [FULL_HD]: 56,
};

export const GAP = {
  [HD]: 12,
  [FULL_HD]: 24,
};

export const BORDER_RADIUS = {
  [HD]: 4,
  [FULL_HD]: 4,
};

export const getIconViewBox = _memoize(screenSize => ({
  h: ICON_SIZES[screenSize],
}));

export const getInitialStyle = ({ screenSize, colorMode } = defaultContext) => {
  const { w, h } = MOSAIC_CARD_SIZE[screenSize];
  const borderSize = BORDER_WIDTH[screenSize];
  const borderOffset = borderSize * 2;

  return {
    Border: {
      w: w + borderOffset,
      h: h + borderOffset,
      x: -borderSize,
      y: -borderSize,
      color: TRANSPARENT,
      shader: {
        radius: BORDER_RADIUS[screenSize],
        outline: BORDER_WIDTH[screenSize],
        strokeColor: TRANSPARENT,
        innerColor: TRANSPARENT,
      },
    },
    Background: {
      w,
      h,
      shader: {
        fillColor: colors.background[colorMode],
        radius: BORDER_RADIUS[screenSize],
      },
    },
    Image: {
      w,
      h,
      shader: {
        radius: BORDER_RADIUS[screenSize],
      },
    },
    Content: {
      w,
      h,
      flex: {
        direction: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      },
      IconWrapper: {
        w,
        h: ICON_SIZES[screenSize] + GAP[screenSize],
        Icon: {
          mountX: 0.5,
          x: middle,
          color: colors.icon[colorMode],
        },
      },
      Label: {
        color: colors.label[colorMode],
        text: {
          ...getFont(FONT.BERLIN_2, screenSize),
          wordWrapWidth: w - PADDING * 2,
          wordWrap: true,
          wordBreak: true,
          advancedRenderer: true,
          textAlign: 'center',
          verticalAlign: 'bottom',
          maxLines: 2,
          maxLinesSuffix: '...',
        },
      },
    },
  };
};

export const getFocusedStyle = ({ colorMode }) => ({
  Border: {
    smooth: {
      scale: SCALE_FOCUSED,
      'shader.strokeColor': BORDER_COLOR[colorMode],
      'shader.innerColor': INNER_BORDER_COLOR[colorMode],
      'shader.scale': SCALE_FOCUSED,
    },
  },
  Background: {
    smooth: {
      scale: SCALE_FOCUSED,
      'shader.scale': SCALE_FOCUSED,
      'shader.fillColor': colors.backgroundFocused[colorMode],
    },
  },
  Image: {
    smooth: {
      scale: SCALE_FOCUSED,
      'shader.scale': SCALE_FOCUSED,
    },
  },
});

export const getUnfocusedStyle = ({ colorMode }) => ({
  Border: {
    smooth: {
      scale: 1,
      'shader.strokeColor': TRANSPARENT,
      'shader.innerColor': TRANSPARENT,
      'shader.scale': 1.0,
    },
  },
  Background: {
    smooth: {
      scale: 1,
      'shader.fillColor': colors.background[colorMode],
      'shader.scale': 1.0,
    },
  },
  Image: {
    smooth: {
      scale: 1,
      'shader.scale': 1.0,
    },
  },
});
